import { addDays, datesHaveSameDayMonthYear, formatDateISO, isBefore, isBetween } from '~/services/TimeService';

// Helper functions to replace Moment.js

/**
 * Returns true if date is today or in the future (same or after today)
 * Equivalent to moment.utc(date, ISO_DATE_FORMAT).isSameOrAfter(moment.utc(), 'day')
 */
function afterToday(date: string | Date): boolean {
  return !isBefore(date);
}

/**
 * Returns true if date is in the past (before today)
 * Equivalent to moment.utc(date, ISO_DATE_FORMAT).isBefore(moment.utc(), 'day')
 */
function inThePast(date: string | Date): boolean {
  return isBefore(date);
}

/**
 * Checks if an experience is standalone (not an upsell to accommodation)
 * Standalone means the order doesn't have accommodation_items or bedbank_items
 */
function isStandaloneExperience(item: App.ExperienceItem, order: App.Order): boolean {
  return !order.accommodation_items?.length && !order.bedbank_items?.length;
}

/**
 * Checks if an order only contains experience items
 */
function isOnlyExperiencesOrder(order: App.Order): boolean {
  const hasOtherItems =
    order.items?.length > 0 ||
    order.bedbank_items?.length > 0 ||
    order.tour_items?.length > 0 ||
    order.cruise_items?.length > 0 ||
    order.flight_items?.length > 0 ||
    order.car_hire_items?.length > 0 ||
    order.custom_offer_items?.length > 0 ||
    order.insurance_items?.length > 0 ||
    order.merchandise_items?.length > 0;

  return !hasOtherItems && (order.experience_items?.length > 0 || false);
}

// Item-specific functions

export function isItemCurrentOrCloseToCheckIn(item: App.OrderItem) {
  if (item.status === 'cancelled') {
    return false;
  }

  if (!item.reservation) {
    return false;
  }

  const checkIn = item.reservation.check_in || item.reservation.start_date;
  if (!checkIn) {
    return false;
  }

  const twoDaysFromNow = formatDateISO(addDays(2));
  return isItemCurrent(item) || (isItemUpcoming(item) && !isBefore(checkIn, twoDaysFromNow));
}

export function isItemBedbankCurrentOrCloseToCheckIn(item: App.AnyItem) {
  if (!('check_in' in item) || !('check_out' in item)) {
    return false;
  }
  const bedbankItem = item;
  if (bedbankItem.status === 'cancelled') {
    return false;
  }

  if (!bedbankItem.check_in) {
    return false;
  }

  const twoDaysFromNow = formatDateISO(addDays(2));
  return (
    isItemBedbankCurrent(bedbankItem) ||
    (isItemBedbankUpcoming(bedbankItem) && !isBefore(bedbankItem.check_in, twoDaysFromNow))
  );
}

export function isItemUpcoming(item: App.OrderItem) {
  if (item.status === 'cancelled') {
    return false;
  }

  const reservation = item.reservation;
  if (!item.reservation_made || !reservation) {
    // If no reservation made, check book_by_date
    if (item.offer?.book_by_date && inThePast(item.offer.book_by_date)) {
      return false;
    }
    return true;
  }

  const checkIn = reservation.check_in || reservation.start_date;
  return checkIn ? afterToday(checkIn) : false;
}

export function isItemBedbankUpcoming(item: App.AnyItem) {
  if (!('check_in' in item) || !('check_out' in item)) {
    return false;
  }
  // TypeScript doesn't know these properties exist, but we've checked with 'in'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bedbankItem = item;
  // downgradeType does not always imply an item is inactive
  // some items are a date change with the downgradeType tag
  // we must also check for isDowngraded tag
  // should be done at mapper level https://aussiecommerce.atlassian.net/browse/CRO-4508
  if (bedbankItem.status === 'cancelled' || (bedbankItem.downgradeType && bedbankItem.isDowngraded)) {
    return false;
  }

  return bedbankItem.check_in ? afterToday(bedbankItem.check_in) : false;
}

export function isItemTourUpcoming(item: App.OrderTourItem) {
  if (item.status === 'cancelled') {
    return false;
  }

  return item.start_date ? afterToday(formatDateISO(item.start_date)) : false;
}

function isInsuranceUpcoming(item: App.InsuranceItem) {
  return item.end_date ? afterToday(item.end_date) : false;
}

export function isFlightUpcoming(item: App.FlightItem) {
  if (!item.journey?.departing?.flights?.length) {
    return false;
  }

  // Get earliest departing flight date
  const flights = item.journey.departing.flights;
  const earliestDate = new Date(Math.min(...flights.map((flight) => new Date(flight.departing_date).getTime())));
  return afterToday(formatDateISO(earliestDate));
}

function isItemCurrent(item: App.OrderItem) {
  if (item.status === 'cancelled') {
    return false;
  }

  if (item.reservation) {
    const checkIn = item.reservation.check_in || item.reservation.start_date;
    const checkOut = item.reservation.check_out || item.reservation.end_date;
    if (checkIn && checkOut) {
      return isBetween(checkIn, checkOut, undefined, 'day', '[]');
    }
  }
  return false;
}

function isItemBedbankCurrent(item: App.AnyItem) {
  if (!('check_in' in item) || !('check_out' in item)) {
    return false;
  }
  // TypeScript doesn't know these properties exist, but we've checked with 'in'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bedbankItem = item;

  if (bedbankItem.status === 'cancelled') {
    return false;
  }

  if (bedbankItem.check_in && bedbankItem.check_out) {
    return isBetween(bedbankItem.check_in, bedbankItem.check_out, undefined, 'day', '[]');
  }
  return false;
}

function isInsuranceCurrent(item: App.InsuranceItem) {
  if (item.status === 'cancelled') {
    return false;
  }

  if (item.start_date && item.end_date) {
    return isBetween(item.start_date, item.end_date, undefined, 'day', '[]');
  }
  return false;
}

function isFlightCurrent(item: App.FlightItem) {
  if (item.status === 'cancelled') {
    return false;
  }

  if (!item.journey?.departing?.flights?.length) {
    return false;
  }

  // Get earliest departing and latest returning dates
  const departingFlights = item.journey.departing.flights;
  const earliestDeparture = new Date(
    Math.min(...departingFlights.map((flight) => new Date(flight.departing_date).getTime())),
  );

  const returningFlights = item.journey.returning?.flights;
  if (!returningFlights?.length) {
    return false;
  }

  const latestReturn = new Date(
    Math.max(...returningFlights.map((flight) => new Date(flight.departing_date).getTime())),
  );

  return isBetween(formatDateISO(earliestDeparture), formatDateISO(latestReturn), undefined, 'day', '[]');
}

// this always returns true for open date standalone experiences but correct way to classify this is by markedAsRedeemed, but that is not available in the order object
export function isExperienceItemUpcoming(item: App.ExperienceItem, order: App.Order) {
  if (item.status === 'cancelled') {
    return false;
  }

  // this can be an open date experiences as well, but we consider it as not upcoming if the expiry date in the past with status as completed
  if (isExperienceItemExpired(item)) {
    return false;
  }

  // always consider standalone open date experiences as upcoming. If non-standalone return false so isUpcoming function will check the other items
  // the correct way to classify this is by markedAsRedeemed, but that is not available in the order object
  if (!item.ticket?.date) {
    return isStandaloneExperience(item, order);
  }

  return afterToday(formatDateISO(item.ticket.date));
}

export function isCustomOfferActive(item: App.CustomOfferOrderItem) {
  if (item.status === 'cancelled') {
    return false;
  }
  if (item.custom_offer && item.custom_offer.items.length > 0 && item.custom_offer.items[0].end_date) {
    return afterToday(item.custom_offer.items[0].end_date);
  }
  return true;
}

export function isTransferItemUpcoming(item: App.ExperienceItem) {
  // Transfers are experience items with specific ticket types
  return item.status !== 'cancelled' && item.ticket?.date && afterToday(formatDateISO(item.ticket.date));
}

export function isExperienceItemExpired(item: App.ExperienceItem) {
  return item.status === 'completed' && item.expiration_date && inThePast(item.expiration_date);
}

export function isExperienceItemCurrent(item: App.ExperienceItem) {
  return (
    item.status !== 'cancelled' &&
    item.ticket?.date &&
    datesHaveSameDayMonthYear(item.ticket.date) &&
    !isExperienceItemExpired(item)
  );
}

export function isTransferItemCurrent(item: App.ExperienceItem) {
  return item.status !== 'cancelled' && item.ticket?.date && datesHaveSameDayMonthYear(item.ticket.date);
}

export function isCustomOfferItemCurrent(item: App.CustomOfferOrderItem) {
  if (item.status === 'cancelled') {
    return false;
  }

  if (
    item.custom_offer &&
    item.custom_offer.items.length > 0 &&
    item.custom_offer.items[0].end_date &&
    item.custom_offer.items[0].start_date
  ) {
    return isBetween(
      item.custom_offer.items[0].start_date,
      item.custom_offer.items[0].end_date,
      undefined,
      'day',
      '[]',
    );
  }
  return true;
}

export function isItemTourCurrent(item: App.OrderTourItem) {
  if (item.status === 'cancelled') {
    return false;
  }

  if (item.start_date && item.end_date) {
    return isBetween(formatDateISO(item.start_date), formatDateISO(item.end_date), undefined, 'day', '[]');
  }
  return false;
}

export function isCruiseItemUpcoming(item: App.CruiseOrderItem) {
  if (item.status === 'cancelled') {
    return false;
  }
  // Note: departure_date exists at runtime but isn't in the type definition
  const cruiseItem = item as App.CruiseOrderItem & { departure_date?: string };
  return cruiseItem.departure_date ? afterToday(cruiseItem.departure_date) : false;
}

export function isCarHireItemUpcoming(item: App.CarHireItem) {
  if (item.status === 'cancelled' || !item.reservation?.pickUp?.date) {
    return false;
  }
  return afterToday(item.reservation.pickUp.date);
}

export function isCruiseItemCurrent(item: App.CruiseOrderItem) {
  if (item.status === 'cancelled') {
    return false;
  }
  // Note: departure_date and arrival_date exist at runtime
  const cruiseItem = item as App.CruiseOrderItem & { departure_date?: string; arrival_date?: string };
  if (cruiseItem.departure_date && cruiseItem.arrival_date) {
    return isBetween(cruiseItem.departure_date, cruiseItem.arrival_date, undefined, 'day', '[]');
  }
  return false;
}

export function isCarHireItemCurrent(item: App.CarHireItem) {
  if (item.status === 'cancelled' || !item.reservation?.pickUp?.date) {
    return false;
  }
  const returnDate = item.reservation?.return?.date;
  if (returnDate && typeof returnDate === 'string') {
    return isBetween(item.reservation.pickUp.date, returnDate, undefined, 'day', '[]');
  }
  return false;
}

export function isMerchandiseItemUpcoming(item: App.MerchandiseItem) {
  if (item.status === 'cancelled') {
    return false;
  }
  // TODO: not implemented yet, I believe we won't be even looking at 'upcoming' or not in future changes
  return true;
}

export function isMerchandiseItemCurrent(item: App.MerchandiseItem) {
  if (item.status === 'cancelled') {
    return false;
  }
  // TODO: not implemented yet, I believe we won't be even looking at 'upcoming' or not in future changes
  return true;
}

export function isMerchandiseItemPast(item: App.MerchandiseItem) {
  if (item.status === 'cancelled') {
    return false;
  }
  // TODO: not implemented yet, I believe we won't be even looking at 'upcoming' or not in future changes
  return false;
}

/**
 * Determines if a given order item is "current".
 *
 * In this context, an item is considered "current" if its scheduled window (e.g., for hotel, flight, cruise, hire car, experience, etc.)
 * includes today's date ("now is between start and end date inclusive"), and the item status is not 'cancelled'.
 *
 * "Current" typically means the item is actively in progress or valid for use today, rather than upcoming or past.
 *
 * Each item-type's "current" definition may vary slightly (e.g., merchandise may always return true).
 *
 * See individual item functions for specifics.
 */
export function isOrderCurrent(order: App.Order) {
  return (
    order.items?.some(isItemCurrent) ||
    order.bedbank_items?.some(isItemBedbankCurrent) ||
    order.insurance_items?.some(isInsuranceCurrent) ||
    order.flight_items?.some(isFlightCurrent) ||
    order.tour_items?.some(isItemTourCurrent) ||
    order.experience_items?.some(isExperienceItemCurrent) ||
    order.cruise_items?.some(isCruiseItemCurrent) ||
    order.car_hire_items?.some(isCarHireItemCurrent) ||
    // Transfers are experience items, so check them within experience_items
    order.custom_offer_items?.some(isCustomOfferItemCurrent) ||
    order.merchandise_items?.some(isMerchandiseItemCurrent)
  );
}

// Reusing logic to determine if Velocity post order eligibility
export function isUpcomingForPartnerVelocityEarnEligibility(order: App.Order) {
  return (
    order.items?.some(isItemUpcoming) ||
    order.tour_items?.some(isItemTourUpcoming) ||
    order.experience_items?.some((item) => isExperienceItemUpcoming(item, order)) ||
    order.cruise_items?.some(isCruiseItemUpcoming)
  );
}

/**
 * "Upcoming" refers to any order that contains at least one item whose scheduled use or validity
 * is in the future (i.e., not completed, expired, or cancelled). This includes hotel bookings with check-in dates in the future,
 * flights departing in the future, tours or experiences with event dates yet to start, cruises scheduled for later, and similar.
 *
 * An item is generally considered "upcoming" if:
 *   - Its start or validity date is in the future relative to now,
 *   - Its status is not 'cancelled',
 *   - The item is not otherwise expired or marked as "used/redeemed".
 *
 * Note:
 * For open-date standalone experiences (where the guest can redeem at any future date), this logic will always return true
 * (i.e., they are always "upcoming") because the current implementation lacks access to a "markedAsRedeemed" field
 * in the order object to determine whether it has already been used. Correct classification would require that additional info.
 */
export function isUpcoming(order: App.Order) {
  return (
    order.items?.some(isItemUpcoming) ||
    order.bedbank_items?.some(isItemBedbankUpcoming) ||
    order.insurance_items?.some(isInsuranceUpcoming) ||
    order.flight_items?.some(isFlightUpcoming) ||
    order.tour_items?.some(isItemTourUpcoming) ||
    order.experience_items?.some((item) => isExperienceItemUpcoming(item, order)) ||
    order.cruise_items?.some(isCruiseItemUpcoming) ||
    order.car_hire_items?.some(isCarHireItemUpcoming) ||
    // Transfers are experience items, so they're covered above
    order.custom_offer_items?.some(isCustomOfferActive) ||
    order.merchandise_items?.some(isMerchandiseItemUpcoming)
  );
}

export function isCurrentOrCloseToCheckIn(order: App.Order) {
  return (
    order.items?.some(isItemCurrentOrCloseToCheckIn) || order.bedbank_items?.some(isItemBedbankCurrentOrCloseToCheckIn)
  );
}

export function isOrderPast(order: App.Order) {
  return !isUpcoming(order) && !isOrderCurrent(order);
}

export function isOnlyExperiencesOrderUpcoming(order: App.Order) {
  if (isOnlyExperiencesOrder(order)) {
    return order.experience_items?.some((item) => {
      if (item.status === 'cancelled') {
        return false;
      }

      if (isExperienceItemExpired(item)) {
        return false;
      }

      if (!item.ticket?.date) {
        return true;
      }

      return afterToday(formatDateISO(item.ticket.date));
    });
  }

  return false;
}
